import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from './misc';

export function PageHeader({ title, description, actions, eyebrow, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; eyebrow?: ReactNode; className?: string }) {
  return (
    <div className={cn('mb-6 flex flex-wrap items-end justify-between gap-4', className)}>
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-[12px] font-medium uppercase tracking-[0.12em] text-primary">{eyebrow}</div>}
        <h1 className="font-display text-[28px] font-semibold leading-tight">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-[13.5px] text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, icon, trend, loading, tone = 'default', onClick }: { label: ReactNode; value: ReactNode; hint?: ReactNode; icon?: ReactNode; trend?: number | null; loading?: boolean; tone?: 'default' | 'primary' | 'warning' | 'danger' | 'success'; onClick?: () => void }) {
  const toneCls = { default: 'bg-muted text-foreground/70', primary: 'bg-primary/10 text-primary', warning: 'bg-warning/10 text-warning', danger: 'bg-destructive/10 text-destructive', success: 'bg-success/10 text-success' }[tone];
  return (
    <div onClick={onClick} className={cn('card-surface group relative overflow-hidden p-5 transition-shadow', onClick && 'cursor-pointer hover:shadow-lift')}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-[13px] font-medium text-muted-foreground">{label}</p>
        {icon && <div className={cn('grid size-8 place-items-center rounded-lg [&_svg]:size-4', toneCls)}>{icon}</div>}
      </div>
      {loading ? <Skeleton className="mt-3 h-8 w-24" /> : <div className="mt-2 font-display text-[28px] font-semibold leading-none tabular">{value}</div>}
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        {typeof trend === 'number' && (
          <span className={cn('inline-flex items-center gap-0.5 font-medium', trend >= 0 ? 'text-success' : 'text-destructive')}>
            {trend >= 0 ? <ArrowUpRight className="size-3.5" /> : <ArrowDownRight className="size-3.5" />}
            {Math.abs(Math.round(trend * 100))}%
          </span>
        )}
        {hint}
      </div>
    </div>
  );
}

export function Section({ title, description, actions, children, className }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('space-y-3', className)}>
      {(title || actions) && (
        <div className="flex items-end justify-between gap-3">
          <div>
            {title && <h2 className="text-[15px] font-semibold">{title}</h2>}
            {description && <p className="text-[13px] text-muted-foreground">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/** Label/value list for detail views. */
export function DescriptionList({ items, columns = 2 }: { items: Array<{ label: ReactNode; value: ReactNode; span?: boolean }>; columns?: 1 | 2 | 3 }) {
  return (
    <dl className={cn('grid gap-x-6 gap-y-4', { 1: 'grid-cols-1', 2: 'grid-cols-1 sm:grid-cols-2', 3: 'grid-cols-1 sm:grid-cols-3' }[columns])}>
      {items.map((it, i) => (
        <div key={i} className={cn('min-w-0', it.span && 'sm:col-span-full')}>
          <dt className="text-[12px] font-medium uppercase tracking-wide text-muted-foreground">{it.label}</dt>
          <dd className="mt-1 break-words text-[13.5px]">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}
